import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import TopBar from "../components/common/TopBar";
import "./PrivacyPolicyPage.css";

const SECTION_CONFIG = [
  { id: "overview", key: "overview" },
  { id: "data-collected", key: "dataCollected" },
  { id: "communications", key: "communications" },
  { id: "verification", key: "verification" },
  { id: "storage-media", key: "storageMedia" },
  { id: "economy", key: "economy" },
  { id: "withdrawals", key: "withdrawals" },
  { id: "moderation", key: "moderation" },
  { id: "support-notifications", key: "supportNotifications" },
  { id: "providers", key: "providers" },
  { id: "security", key: "security" },
  { id: "retention", key: "retention" },
  { id: "rights", key: "rights" },
  { id: "contact", key: "contact" },
];

function splitParagraphs(text) {
  if (!text) return [];
  return String(text)
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean);
}

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const isRtl = useMemo(() => {
    const lng = String(i18n.language || "").toLowerCase();
    return lng.startsWith("ar") || lng.startsWith("fa") || lng.startsWith("he");
  }, [i18n.language]);

  const scrollToId = useCallback((hashId) => {
    const el = document.getElementById(hashId);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const pageTitle = t("legal.privacyPolicy.pageTitle", {
    defaultValue: "Privacy policy",
  });

  return (
    <div className="pp-page" dir={isRtl ? "rtl" : "ltr"} lang={i18n.language}>
      <TopBar title={pageTitle} onBack={() => navigate(-1)} />

      <div className="pp-inner">
        <header className="pp-hero">
          <span className="pp-badge" aria-hidden>
            Livora
          </span>
          <h1 className="pp-title">{pageTitle}</h1>
          <p className="pp-updated">
            {t("legal.privacyPolicy.lastUpdated", {
              defaultValue: "Last updated: May 15, 2026",
            })}
          </p>
          <p className="pp-lead">
            {t("legal.privacyPolicy.lead", {
              defaultValue:
                "This policy describes how Livora (\"we\", \"us\") handles personal information when you use the platform, including calls, messages, verification, wallet features, and support.",
            })}
          </p>
        </header>

        <nav
          className="pp-nav-wrap"
          aria-label={t("legal.privacyPolicy.navLabel", { defaultValue: "On this page" })}
        >
          <div className="pp-nav">
            {SECTION_CONFIG.map(({ id, key }) => (
              <button
                key={id}
                type="button"
                className="pp-nav-link"
                onClick={() => scrollToId(`pp-${id}`)}
              >
                {t(`legal.privacyPolicy.nav.${key}`, {
                  defaultValue: key,
                })}
              </button>
            ))}
          </div>
        </nav>

        <main>
          {SECTION_CONFIG.map(({ id, key }) => {
            const title = t(`legal.privacyPolicy.sections.${key}.title`, {
              defaultValue: "",
            });
            const body = t(`legal.privacyPolicy.sections.${key}.body`, {
              defaultValue: "",
            });
            const bulletsRaw = t(`legal.privacyPolicy.sections.${key}.bullets`, {
              defaultValue: [],
              returnObjects: true,
            });
            const bullets =
              Array.isArray(bulletsRaw) && bulletsRaw.every((x) => typeof x === "string")
                ? bulletsRaw
                : [];

            return (
              <section
                key={id}
                id={`pp-${id}`}
                className="pp-section"
                aria-labelledby={`pp-h-${id}`}
              >
                <article className="pp-card">
                  <h2 id={`pp-h-${id}`}>{title}</h2>
                  {splitParagraphs(body).map((para, idx) => (
                    <p key={idx}>{para}</p>
                  ))}
                  {bullets.length > 0 ? (
                    <ul className="pp-list">
                      {bullets.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  ) : null}
                </article>
              </section>
            );
          })}
        </main>

        <footer className="pp-footnote">
          {t("legal.privacyPolicy.disclaimer", {
            defaultValue:
              "This policy is provided for transparency. It is not legal advice. Depending on your country or region, additional rights or disclosures may apply. If you have questions, contact us through in-app support.",
          })}
        </footer>
      </div>
    </div>
  );
}
